import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { findCaseBySlug } from '../../utils/slugify';

interface AssetSection {
  id: string;
  type: 'video' | 'image' | 'carousel';
  element: HTMLElement;
  title?: string;
}

const CaseStudyPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState<any>(null);
  const [assetSections, setAssetSections] = useState<AssetSection[]>([]);
  const [activeSection, setActiveSection] = useState<number>(0);
  const [isNavSticky, setIsNavSticky] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);

  // Function to inject navigation into HTML string
  const injectAssetWellNavigation = (htmlContent: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const assetWell = doc.querySelector('.assetWell');
    
    if (!assetWell) return htmlContent;

    // Remove existing navigation
    const existingNav = assetWell.querySelector('.assetWellNav');
    if (existingNav) {
      existingNav.remove();
    }

    // Count navigable sections
    const sections = assetWell.querySelectorAll('.imageCallout, .caseVideo, .imageCarousel');
    
    if (sections.length === 0) return htmlContent;

    // Create navigation HTML
    const navHTML = `
      <div class="asset-well-nav natural" data-react-nav="true">
        <div class="nav-line"></div>
        ${Array.from(sections).map((_, index) => 
          `<button class="nav-item ${index === 0 ? 'active' : ''}" data-section="${index}" title="Section ${index + 1}">${index + 1}</button>`
        ).join('')}
      </div>
    `;

    // Insert navigation at the beginning of assetWell
    assetWell.insertAdjacentHTML('afterbegin', navHTML);

    return doc.documentElement.outerHTML;
  };
  useEffect(() => {
    if (!slug) {
      navigate('/');
      return;
    }

    const foundCase = findCaseBySlug(slug);
    if (!foundCase) {
      navigate('/');
      return;
    }

    setCaseData(foundCase);

    const loadContent = async () => {
      try {
        // Try to import the content directly in development
        const response = await fetch(`/portfolio-content/${foundCase.contentFile}.html`, {
          cache: 'no-cache',
          headers: {
            'Accept': 'text/html,*/*'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to load content: ${response.status}`);
        }
        
        let htmlContent = await response.text();
        
        // If we got the React app instead of the content file, show error
        if (htmlContent.includes('<!DOCTYPE html>') && htmlContent.includes('<div id="root">')) {
          throw new Error('Dev server routing conflict - got React app instead of content file');
        }
        
        // Process content to inject navigation if assetWell exists
        if (htmlContent.includes('class="assetWell"')) {
          htmlContent = injectAssetWellNavigation(htmlContent);
        }
        
        setContent(htmlContent);
      } catch (error) {
        console.error('Error loading case content:', error);
        setContent('<p>Error loading content.</p>');
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [slug, navigate]);

  // Setup navigation behavior after content loads
  useEffect(() => {
    if (!loading && contentRef.current && content) {
      const timer = setTimeout(() => {
        const assetWell = contentRef.current?.querySelector('.assetWell') as HTMLElement;
        const navElement = contentRef.current?.querySelector('.asset-well-nav') as HTMLElement;
        
        if (!assetWell || !navElement) {
          console.log('No assetWell or navigation found');
          return;
        }

        console.log('Found navigation element:', navElement);

        // Get all navigable sections
        const sections = Array.from(assetWell.querySelectorAll('.imageCallout, .caseVideo, .imageCarousel')) as HTMLElement[];
        
        if (sections.length === 0) return;

        // Add click handlers to navigation buttons
        const navButtons = navElement.querySelectorAll('.nav-item') as NodeListOf<HTMLButtonElement>;
        navButtons.forEach((button, index) => {
          button.addEventListener('click', () => handleNavClick(index));
        });

        // Setup observers
        const header = document.querySelector('.case-study-header') as HTMLElement;
        const headerHeight = header?.offsetHeight || 0;
        let observers: IntersectionObserver[] = [];
        
        // Sticky navigation observer
        const stickyObserver = new IntersectionObserver(
          ([entry]) => {
            const assetWellRect = entry.boundingClientRect;
            const navTopOffset = 40; // Navigation's natural top position within assetWell
            const navAbsoluteTop = assetWellRect.top + navTopOffset;
            const shouldBeSticky = navAbsoluteTop <= headerHeight + 50;
            
            setIsNavSticky(shouldBeSticky);
            
            if (shouldBeSticky) {
              // Calculate how far down to position the nav within assetWell to keep it visible
              const scrollOffset = Math.max(0, window.scrollY - assetWell.offsetTop + headerHeight + 20);
              navElement.className = 'asset-well-nav sticky';
              navElement.style.top = `${scrollOffset}px`;
              console.log('Sticky mode: positioning nav at', scrollOffset, 'px within assetWell');
            } else {
              navElement.className = 'asset-well-nav natural';
              navElement.style.top = '40px';
              console.log('Natural mode: nav at 40px');
            }
          },
          { 
            threshold: [0, 0.1, 0.5, 1],
            rootMargin: `0px 0px 0px 0px`
          }
        );
        
        stickyObserver.observe(assetWell);
        observers.push(stickyObserver);
        
        // Add scroll listener to update sticky position
        const handleScroll = () => {
          if (isNavSticky) {
            const scrollOffset = Math.max(0, window.scrollY - assetWell.offsetTop + headerHeight + 20);
            navElement.style.top = `${scrollOffset}px`;
          }
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        
        // Section highlighting observer
        const sectionObserver = new IntersectionObserver(
          (entries) => {
            // Find the section that's most visible in the top half of viewport
            let mostVisibleSection = -1;
            let maxVisibility = 0;
            
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const rect = entry.boundingClientRect;
                const viewportTop = headerHeight + 20;
                const viewportMiddle = window.innerHeight / 2;
                
                // Calculate how much of the section is in the "focus area" (top half of viewport)
                const focusAreaTop = Math.max(rect.top, viewportTop);
                const focusAreaBottom = Math.min(rect.bottom, viewportMiddle);
                const visibleHeight = Math.max(0, focusAreaBottom - focusAreaTop);
                
                if (visibleHeight > maxVisibility) {
                  maxVisibility = visibleHeight;
                  mostVisibleSection = sections.indexOf(entry.target as HTMLElement);
                }
              }
            });
            
            // Update active section if we found one
            if (mostVisibleSection !== -1) {
              console.log('Active section:', mostVisibleSection + 1, 'of', sections.length);
              setActiveSection(mostVisibleSection);
              navButtons.forEach((btn, idx) => {
                btn.classList.toggle('active', idx === mostVisibleSection);
              });
            }
          },
          {
            threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
            rootMargin: `0px 0px 0px 0px`
          }
        );
        
        sections.forEach((section) => {
          sectionObserver.observe(section);
        });
        observers.push(sectionObserver);
        
        // Cleanup
        return () => {
          observers.forEach(observer => observer.disconnect());
          window.removeEventListener('scroll', handleScroll);
          navButtons.forEach((button, index) => {
            button.removeEventListener('click', () => handleNavClick(index));
          });
        };
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [loading, content]);

  const handleBackClick = () => {
    navigate('/case-studies');
  };

  const handleNavClick = (sectionIndex: number) => {
    if (!contentRef.current) return;
    
    const sections = Array.from(contentRef.current.querySelectorAll('.assetWell .imageCallout, .assetWell .caseVideo, .assetWell .imageCarousel')) as HTMLElement[];
    const targetSection = sections[sectionIndex];
    
    if (targetSection) {
      const headerHeight = (document.querySelector('.case-study-header') as HTMLElement)?.offsetHeight || 0;
      const offsetTop = targetSection.offsetTop - headerHeight - 20;
      
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
      
      setActiveSection(sectionIndex);
    }
  };

  if (!caseData) {
    return null;
  }

  return (
    <div className="case-study-page-container">
      <button
        className="case-study-close"
        onClick={handleBackClick}
        aria-label="Close case study and return to case studies"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M6 6l12 12M18 6L6 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
      
      <svg className="case-study-header-logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 442.5 442.5">
        <defs>
          <style>{`.cls-1{fill:#000;}`}</style>
        </defs>
        <g id="Layer_2" data-name="Layer 2">
          <g id="Layer_1-2" data-name="Layer 1">
            <path className="cls-1" d="M271.25,0V50H392.5V222.7a168.69,168.69,0,0,1-49.73,120.07A171.68,171.68,0,0,1,321.25,361v58.2A219.79,219.79,0,0,0,442.5,222.7V0Z"/>
            <path className="cls-1" d="M171.25,292.5h50v100A172.67,172.67,0,0,1,50,219.8,169.79,169.79,0,0,1,219.8,50h1.45V0H219.8C98.41,0,0,98.41,0,219.8H0C0,342.31,98.93,441.7,221.25,442.48v0h50v-200h-100Z"/>
          </g>
        </g>
      </svg>

      <div className="case-study-content" ref={contentRef}>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <div dangerouslySetInnerHTML={{ __html: content }} />
        )}
        
        
        <div className="case-study-footer">
          <img src="/assets/images/gj_footer.png" alt="Greg Johnson" />
        </div>
      </div>
    </div>
  );
};

export default CaseStudyPage;